import {
  COUNTRY_CONFIG,
  LANGUAGE_CONFIG,
  LANGUAGE_URL_PREFIXES,
  SITE_COUNTRY_CODE,
  SITE_COUNTRY_CODE_REGEX,
  SITE_ID
} from 'Douleutaras/constants/localization';
import memoize from 'lodash/memoize';
import { matchPath } from 'react-router';
import { getRequestContext } from 'Douleutaras/services/requestContext';
import cookie from 'js-cookie';
import { LANGUAGE_CODES, PAYMENT_BANK } from 'Douleutaras/constants/system';
import { CURRENCY_FORMAT } from 'Douleutaras/configuration/formatters';
import { Gis } from 'Douleutaras/components/Forms/gis.class';
import { ANONYMIZED_PATHS } from 'Douleutaras/constants/urls';

type MomentLocale = (language: LANGUAGE_CODES) => string;
type MomentDisplayFormat = (language: LANGUAGE_CODES) => string;

export interface Formatter {
  currency(
    val: number | string,
    // in case val equals to zero and this flag is true, the function will return "undefined" and not '€0'
    undefinedOnZero?: boolean
  ): string;
  number(val: number | string): string;
  /** Locale-placed currency, keeping the locale's separator: "1.250 €" (el) / "€1,250" (en). */
  price(val: number | string): string;
}

export interface LocaleResponse {
  siteId: number;
  countryCode: SITE_COUNTRY_CODE;
  momentTimezone: string;
  momentLocale: MomentLocale;
  momentDateFormat: MomentDisplayFormat;
  currentLanguage: LANGUAGE_CODES;
  vatRegExp: RegExp;
  paymentMethods: Array<PAYMENT_BANK>;
  paymentMethodUnattended: PAYMENT_BANK;
  formatter: Formatter;
  fbAuthClientId: string;
  localizeLegacyUrls(url: string, language: LANGUAGE_CODES): string;
}

export const useLocalization = (): LocaleResponse => {
  const countryCode = getCountryCode();
  const siteId = SITE_ID[countryCode] as unknown as number;
  const vatRegExp = COUNTRY_CONFIG.VAT_REGEXP[countryCode];
  const paymentMethods = COUNTRY_CONFIG.ONLINE_PAYMENT_BANK[countryCode];
  const paymentMethodUnattended = COUNTRY_CONFIG.ONLINE_PAYMENT_UNATTENDED[countryCode];
  const fbAuthClientId = COUNTRY_CONFIG.FACEBOOK_AUTH_CLIENT_ID[countryCode];

  return {
    siteId,
    vatRegExp,
    countryCode,
    fbAuthClientId,
    paymentMethods,
    currentLanguage: getCurrentLanguage(),
    paymentMethodUnattended,
    momentTimezone: getMomentLocalTimezone(countryCode),
    momentLocale: (language: LANGUAGE_CODES) => getMomentLocale(language),
    momentDateFormat: (language: LANGUAGE_CODES) => getMomentDateFormat(language),
    formatter: getFormatter(),
    localizeLegacyUrls: (url: string, language: LANGUAGE_CODES) => localizeLegacyUrls(url, language)
  };
};

/**
 * Map a domain hostname to country code
 */
export const domainToSiteMapping = (fullHostname: string): SITE_COUNTRY_CODE => {
  switch (true) {
    case Boolean(fullHostname.match(SITE_COUNTRY_CODE_REGEX.GR)):
      return SITE_COUNTRY_CODE.GR;
    case Boolean(fullHostname.match(SITE_COUNTRY_CODE_REGEX.IE)):
      return SITE_COUNTRY_CODE.IE;
    case Boolean(fullHostname.match(SITE_COUNTRY_CODE_REGEX.CY)):
      return SITE_COUNTRY_CODE.CY;
    default:
      return undefined;
  }
};

/**
 * Add languageCode for urls outside react application
 * @TODO: remove when new pros profile will be available
 */

export const localizeLegacyUrls = (originalUrl: string, language: LANGUAGE_CODES) => {
  const supportedLocales = [LANGUAGE_CODES.EN];

  return supportedLocales.includes(language as LANGUAGE_CODES) ? `/en${originalUrl}` : originalUrl;
};

/**
 * Path-prefix helpers for routes cached at the Cloudflare edge (e.g. pro-profile), where
 * language must come from the URL rather than the languagecode cookie. Idempotent: stripping
 * an unprefixed path or prefixing an already-prefixed one is a no-op.
 */
export const stripLanguageUrlPrefix = (pathname: string): string => {
  const prefix = LANGUAGE_URL_PREFIXES.find(p => pathname === `/${p}` || pathname.startsWith(`/${p}/`));

  return prefix ? (pathname.slice(`/${prefix}`.length) || '/') : pathname;
};

export const addLanguageUrlPrefix = (pathname: string): string =>
  `/${LANGUAGE_URL_PREFIXES[0]}${stripLanguageUrlPrefix(pathname)}`;

// True for routes listed in ANONYMIZED_PATHS (e.g. pro-profile) — cached at the Cloudflare edge,
// so language must come from the URL rather than the languagecode cookie. Shared by
// getCurrentLanguage() below (client-side i18n bootstrap), useIsAnonymizedRoute, and
// LanguageSwitcher, so all three agree on exactly the same set of routes.
export const isAnonymizedPathname = (pathname: string): boolean => {
  const bare = stripLanguageUrlPrefix(pathname);

  return ANONYMIZED_PATHS.some(path =>
    Boolean(matchPath({ path, end: false, caseSensitive: false }, bare)));
};

export const getDefaultLanguageCodeByCountryCode = memoize(() => {
  const countryCode = useLocalization().countryCode;

  return COUNTRY_CONFIG.DEFAULT_LANGUAGE[countryCode];
});

/**
 * Extract country code from domain name of the site. Used to determine which country site to use.
 * Memoized on the resolved hostname string so per-country results are cached without
 * cross-request bleed on SSR (each country's hostname maps to a stable result).
 */
const resolveCountryCode = memoize((resolvedHost: string): SITE_COUNTRY_CODE => {
  // Application is not in production mode and SITE_COUNTRY is set explicitly
  const siteCountryCode = (
    process.env.REAL_ENV !== 'production'
    && process.env.REAL_ENV !== 'staging'
    && process.env.SITE_COUNTRY
    && typeof SITE_COUNTRY_CODE[process.env.SITE_COUNTRY] !== 'undefined')

    ? SITE_COUNTRY_CODE[process.env.SITE_COUNTRY]
    : domainToSiteMapping(resolvedHost);

  if (typeof siteCountryCode === 'undefined') {
    throw new Error(`Site country code not detected for domain ${resolvedHost}`);
  }

  return siteCountryCode;
});

export const getCountryCode = (host?: string): SITE_COUNTRY_CODE => {
  const ctx = getRequestContext();
  const resolvedHost = host ?? ctx?.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');

  return resolveCountryCode(resolvedHost);
};

/**
 * (momentjs) Get timezone for local country
 */
export const getMomentLocalTimezone = (countryCode: string) => {

  return COUNTRY_CONFIG.MOMENT.TIMEZONE[countryCode];
};

/**
 * (momentjs) Get locale for local country
 */
export const getMomentLocale = (language: LANGUAGE_CODES) => {
  return LANGUAGE_CONFIG.MOMENT.LOCALE[language];
};

/**
 * (momentjs) Get locale for local country
 */
export const getMomentDateFormat = (language: LANGUAGE_CODES) => {
  return LANGUAGE_CONFIG.MOMENT.DISPLAY_FORMAT.FULL[language];
};

export const getFormatter = (): Formatter => {
  // Delegates to getCurrentLanguage() rather than re-deriving the language locally — this used to
  // have its own copy of that resolution logic, missing the anonymized-route URL-prefix check and
  // causing Intl.NumberFormat to pick a different locale server vs client (e.g. "4.9" vs "4,9").
  const currentLng = getCurrentLanguage();

  const format = (val, style: string) => {
    let data = val;

    if (!val) {
      data = 0;
    }

    return new Intl.NumberFormat(currentLng, { ...CURRENCY_FORMAT, style }).format(data);
  };

  return {
    currency: (val, undefinedOnZero) => undefinedOnZero && Number(val) === 0
      ? undefined
      : format(Number(val), 'currency')
        .replace('\u00A0', ''), // remove extra space between value and currency sign
    number: val => format(Number(val), 'decimal'),
    // Unlike `currency`, keeps the locale's own sign placement and separator: Greek reads
    // "1.250 €" (amount first, non-breaking space), English "€1,250". The space is part of
    // the format, so it is deliberately not stripped here. Cents show in full or not at all —
    // "1.250 €" and "1.250,50 €", never "1.250,5 €".
    price: val => {
      const amount = Number(val) || 0;
      const fractionDigits = Number.isInteger(amount) ? 0 : 2;

      return new Intl.NumberFormat(currentLng, {
        ...CURRENCY_FORMAT,
        style: 'currency',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(amount);
    }
  };
};

// Based on GIS, get address information
export const displayShortAddressPerCountry = (gis: Gis) => {
  const addressComponents = gis?.address.address_components || gis?.address['addressComponents'];
  const addressLocality = addressComponents?.find(addressComponent =>
    addressComponent.types.includes('locality') && addressComponent.types.includes('political'));
  const addressSublocalityNeighborhood = addressComponents?.find(addressComponent =>
    (addressComponent.types.includes('sublocality') || addressComponent.types.includes('neighborhood'))
    && addressComponent.types.includes('political'));

  return {
    [SITE_COUNTRY_CODE.GR]: addressLocality?.long_name || addressLocality?.longName, // ie: Cholargos
    [SITE_COUNTRY_CODE.CY]: gis?.address?.[0]?.full_name || gis?.address?.[0]?.fullName,
    [SITE_COUNTRY_CODE.IE]: addressSublocalityNeighborhood?.long_name || addressSublocalityNeighborhood?.longName
  };
};

// Based on GIS, get address information
export const displayFullAddressPerCountry = (gis: Gis) => {
  const formattedAddress = gis?.address?.['formattedAddress'] || gis?.address?.formatted_address;

  return {
    [SITE_COUNTRY_CODE.GR]: formattedAddress,
    [SITE_COUNTRY_CODE.CY]: gis?.address?.[0]?.full_name || gis?.address?.[0]?.fullName,
    [SITE_COUNTRY_CODE.IE]: formattedAddress
  };
};

export const getCurrentLanguage = (): LANGUAGE_CODES => {
  const ctx = getRequestContext();
  const countryCode = getCountryCode();

  // getRequestContext() is server-only (AsyncLocalStorage) and always undefined on the client.
  // On an anonymized route, the server already ignores the cookie (see server/index.tsx) — the
  // client must derive the same URL-only language here too, or its i18n bootstrap (and anything
  // reading it, e.g. DefaultHeaderLogo's home link) will mismatch what was server-rendered.
  if (!ctx && isAnonymizedPathname(window.location.pathname)) {
    const isPrefixed = window.location.pathname !== stripLanguageUrlPrefix(window.location.pathname);

    return (isPrefixed ? COUNTRY_CONFIG.PREFIXED_LANGUAGE[countryCode] : COUNTRY_CONFIG.DEFAULT_LANGUAGE[countryCode]) as LANGUAGE_CODES;
  }

  const langCode = ctx?.languageCode ?? cookie.get('languagecode');
  const lowerLanguageCode = langCode?.toLowerCase();

  return COUNTRY_CONFIG.LANGUAGES_SUPPORTED[countryCode].includes(lowerLanguageCode) ? lowerLanguageCode
    : COUNTRY_CONFIG.DEFAULT_LANGUAGE[countryCode];
};
